import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/apiClient";
import { compressReceiptLogoToDataUrl } from "@/lib/imageUpload";
import {
  type DeviceServiceSettings,
  type DeviceServiceStatus,
  type PickupReceiptConfig,
  type ReceiptConfig,
  deviceStatusTone,
  formatDeviceStatus,
  getDeviceServiceConnectionHint,
  isDeviceServiceConfigured,
  statusBadgeClass,
} from "@/lib/deviceService";
import {
  DEFAULT_RECEIPT_CHARACTERS_PER_LINE,
  DEFAULT_RECEIPT_FOOTER_ALIGN,
  DEFAULT_RECEIPT_HEADER_ALIGN,
  DEFAULT_RECEIPT_FONT_FAMILY,
  DEFAULT_RECEIPT_FONT_SIZE,
  DEFAULT_RECEIPT_SEPARATOR_STYLE,
  DEFAULT_RECEIPT_TITLE_TEXT,
  MAX_RECEIPT_CHARACTERS_PER_LINE,
  MIN_RECEIPT_CHARACTERS_PER_LINE,
  normalizeReceiptFontFamily,
  type ReceiptFontFamily,
  type ReceiptFontSize,
  type ReceiptSeparatorStyle,
  type ReceiptTextAlignment,
} from "@/lib/receiptPreview";
import {
  DEFAULT_PICKUP_RECEIPT_FONT_FAMILY,
  DEFAULT_PICKUP_RECEIPT_FONT_SIZE,
  DEFAULT_PICKUP_RECEIPT_TITLE_TEXT,
} from "@/lib/pickupReceiptPreview";
import {
  DEFAULT_RECEIPT_LOGO_WIDTH_PERCENT,
  RECEIPT_LOGO_WIDTH_PERCENT_OPTIONS,
} from "@/lib/receiptLogoPreview";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { ReceiptPreview } from "@/components/receipt/ReceiptPreview";
import { PickupReceiptPreview } from "@/components/receipt/PickupReceiptPreview";
import { ReceiptTextToolbar } from "@/components/receipt/ReceiptTextToolbar";

interface TenantSettings {
  device_service?: DeviceServiceSettings;
  receipt_config?: ReceiptConfig;
  pickup_receipt_config?: PickupReceiptConfig;
}

interface DeviceServiceIntegrationPanelProps {
  canWrite: boolean;
}

const EMPTY_RECEIPT_CONFIG: ReceiptConfig = {
  header_text: null,
  footer_text: null,
  logo_data: null,
  logo_configured: false,
  characters_per_line: DEFAULT_RECEIPT_CHARACTERS_PER_LINE,
  separator_style: DEFAULT_RECEIPT_SEPARATOR_STYLE,
  header_align: DEFAULT_RECEIPT_HEADER_ALIGN,
  footer_align: DEFAULT_RECEIPT_FOOTER_ALIGN,
  title_text: null,
  font_size: DEFAULT_RECEIPT_FONT_SIZE,
  font_family: DEFAULT_RECEIPT_FONT_FAMILY,
  logo_width_percent: DEFAULT_RECEIPT_LOGO_WIDTH_PERCENT,
};

const EMPTY_PICKUP_RECEIPT_CONFIG: PickupReceiptConfig = {
  header_text: null,
  footer_text: null,
  logo_data: null,
  logo_configured: false,
  characters_per_line: DEFAULT_RECEIPT_CHARACTERS_PER_LINE,
  separator_style: DEFAULT_RECEIPT_SEPARATOR_STYLE,
  header_align: DEFAULT_RECEIPT_HEADER_ALIGN,
  footer_align: DEFAULT_RECEIPT_FOOTER_ALIGN,
  title_text: DEFAULT_PICKUP_RECEIPT_TITLE_TEXT,
  font_size: DEFAULT_PICKUP_RECEIPT_FONT_SIZE,
  font_family: DEFAULT_PICKUP_RECEIPT_FONT_FAMILY,
  logo_width_percent: DEFAULT_RECEIPT_LOGO_WIDTH_PERCENT,
};

export function DeviceServiceIntegrationPanel({ canWrite }: DeviceServiceIntegrationPanelProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [connectionSaving, setConnectionSaving] = useState(false);
  const [connectionSuccess, setConnectionSuccess] = useState(false);
  const [baseUrl, setBaseUrl] = useState("http://127.0.0.1:5050");
  const [apiKey, setApiKey] = useState("");
  const [apiKeyConfigured, setApiKeyConfigured] = useState(false);

  const [receiptSaving, setReceiptSaving] = useState(false);
  const [receiptSuccess, setReceiptSuccess] = useState(false);
  const [headerText, setHeaderText] = useState("");
  const [footerText, setFooterText] = useState("");
  const [logoData, setLogoData] = useState("");
  const [logoRemoved, setLogoRemoved] = useState(false);
  const [logoConfiguredOnServer, setLogoConfiguredOnServer] = useState(false);
  const [charactersPerLine, setCharactersPerLine] = useState(DEFAULT_RECEIPT_CHARACTERS_PER_LINE);
  const [separatorStyle, setSeparatorStyle] = useState<ReceiptSeparatorStyle>(DEFAULT_RECEIPT_SEPARATOR_STYLE);
  const [headerAlign, setHeaderAlign] = useState<ReceiptTextAlignment>(DEFAULT_RECEIPT_HEADER_ALIGN);
  const [footerAlign, setFooterAlign] = useState<ReceiptTextAlignment>(DEFAULT_RECEIPT_FOOTER_ALIGN);
  const [titleText, setTitleText] = useState(DEFAULT_RECEIPT_TITLE_TEXT);
  const [fontSize, setFontSize] = useState<ReceiptFontSize>(DEFAULT_RECEIPT_FONT_SIZE);
  const [fontFamily, setFontFamily] = useState<ReceiptFontFamily>(DEFAULT_RECEIPT_FONT_FAMILY);
  const [logoWidthPercent, setLogoWidthPercent] = useState(DEFAULT_RECEIPT_LOGO_WIDTH_PERCENT);
  const [logoUploading, setLogoUploading] = useState(false);

  const [status, setStatus] = useState<DeviceServiceStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [printTesting, setPrintTesting] = useState(false);
  const [printResult, setPrintResult] = useState<string | null>(null);
  const [includeCardPayment, setIncludeCardPayment] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const [pickupReceiptSaving, setPickupReceiptSaving] = useState(false);
  const [pickupReceiptSuccess, setPickupReceiptSuccess] = useState(false);
  const [pickupHeaderText, setPickupHeaderText] = useState("");
  const [pickupFooterText, setPickupFooterText] = useState("");
  const [pickupLogoData, setPickupLogoData] = useState("");
  const [pickupLogoRemoved, setPickupLogoRemoved] = useState(false);
  const [pickupLogoConfiguredOnServer, setPickupLogoConfiguredOnServer] = useState(false);
  const [pickupCharactersPerLine, setPickupCharactersPerLine] = useState(DEFAULT_RECEIPT_CHARACTERS_PER_LINE);
  const [pickupSeparatorStyle, setPickupSeparatorStyle] = useState<ReceiptSeparatorStyle>(DEFAULT_RECEIPT_SEPARATOR_STYLE);
  const [pickupHeaderAlign, setPickupHeaderAlign] = useState<ReceiptTextAlignment>(DEFAULT_RECEIPT_HEADER_ALIGN);
  const [pickupFooterAlign, setPickupFooterAlign] = useState<ReceiptTextAlignment>(DEFAULT_RECEIPT_FOOTER_ALIGN);
  const [pickupTitleText, setPickupTitleText] = useState(DEFAULT_PICKUP_RECEIPT_TITLE_TEXT);
  const [pickupFontSize, setPickupFontSize] = useState<ReceiptFontSize>(DEFAULT_PICKUP_RECEIPT_FONT_SIZE);
  const [pickupFontFamily, setPickupFontFamily] = useState<ReceiptFontFamily>(DEFAULT_PICKUP_RECEIPT_FONT_FAMILY);
  const [pickupLogoWidthPercent, setPickupLogoWidthPercent] = useState(DEFAULT_RECEIPT_LOGO_WIDTH_PERCENT);
  const [pickupLogoUploading, setPickupLogoUploading] = useState(false);
  const [pickupPrintTesting, setPickupPrintTesting] = useState(false);
  const [pickupPrintResult, setPickupPrintResult] = useState<string | null>(null);
  const pickupLogoInputRef = useRef<HTMLInputElement>(null);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const settings = await api.get<TenantSettings>("/api/v1/settings");
      setBaseUrl(settings.device_service?.base_url ?? "http://127.0.0.1:5050");
      setApiKeyConfigured(settings.device_service?.api_key_configured ?? false);
      setApiKey("");
      const receipt = settings.receipt_config ?? EMPTY_RECEIPT_CONFIG;
      setHeaderText(receipt.header_text ?? "");
      setFooterText(receipt.footer_text ?? "");
      setLogoData(receipt.logo_data ?? "");
      setLogoConfiguredOnServer(receipt.logo_configured ?? false);
      setLogoRemoved(false);
      setCharactersPerLine(receipt.characters_per_line ?? DEFAULT_RECEIPT_CHARACTERS_PER_LINE);
      setSeparatorStyle(receipt.separator_style ?? DEFAULT_RECEIPT_SEPARATOR_STYLE);
      setHeaderAlign(receipt.header_align ?? DEFAULT_RECEIPT_HEADER_ALIGN);
      setFooterAlign(receipt.footer_align ?? DEFAULT_RECEIPT_FOOTER_ALIGN);
      setTitleText(receipt.title_text ?? DEFAULT_RECEIPT_TITLE_TEXT);
      setFontSize(receipt.font_size ?? DEFAULT_RECEIPT_FONT_SIZE);
      setFontFamily(normalizeReceiptFontFamily(receipt.font_family));
      setLogoWidthPercent(receipt.logo_width_percent ?? DEFAULT_RECEIPT_LOGO_WIDTH_PERCENT);
      const pickup = settings.pickup_receipt_config ?? EMPTY_PICKUP_RECEIPT_CONFIG;
      setPickupHeaderText(pickup.header_text ?? "");
      setPickupFooterText(pickup.footer_text ?? "");
      setPickupLogoData(pickup.logo_data ?? "");
      setPickupLogoConfiguredOnServer(pickup.logo_configured ?? false);
      setPickupLogoRemoved(false);
      setPickupCharactersPerLine(pickup.characters_per_line ?? DEFAULT_RECEIPT_CHARACTERS_PER_LINE);
      setPickupSeparatorStyle(pickup.separator_style ?? DEFAULT_RECEIPT_SEPARATOR_STYLE);
      setPickupHeaderAlign(pickup.header_align ?? DEFAULT_RECEIPT_HEADER_ALIGN);
      setPickupFooterAlign(pickup.footer_align ?? DEFAULT_RECEIPT_FOOTER_ALIGN);
      setPickupTitleText(pickup.title_text ?? DEFAULT_PICKUP_RECEIPT_TITLE_TEXT);
      setPickupFontSize(pickup.font_size ?? DEFAULT_PICKUP_RECEIPT_FONT_SIZE);
      setPickupFontFamily(normalizeReceiptFontFamily(pickup.font_family ?? DEFAULT_PICKUP_RECEIPT_FONT_FAMILY));
      setPickupLogoWidthPercent(pickup.logo_width_percent ?? DEFAULT_RECEIPT_LOGO_WIDTH_PERCENT);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Einstellungen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const result = await api.get<DeviceServiceStatus>("/api/v1/settings/device-service/status");
      setStatus(result);
    } catch (e) {
      setStatus({
        connected: false,
        error: e instanceof Error ? e.message : "Status konnte nicht geladen werden.",
        health: null,
        devices: [],
        zvt: null,
      });
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    void loadStatus();
    const interval = window.setInterval(() => {
      void loadStatus();
    }, 10_000);
    return () => window.clearInterval(interval);
  }, [loadStatus]);

  async function onSaveConnection(e: React.FormEvent) {
    e.preventDefault();
    if (!canWrite) return;
    setConnectionSaving(true);
    setConnectionSuccess(false);
    setError(null);
    try {
      const body: { base_url: string | null; api_key?: string } = {
        base_url: baseUrl.trim() || null,
      };
      if (apiKey.trim()) {
        body.api_key = apiKey;
      } else if (!apiKeyConfigured) {
        body.api_key = "";
      }
      await api.patch("/api/v1/settings/device-service", body);
      setConnectionSuccess(true);
      await loadSettings();
      await loadStatus();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Verbindungseinstellungen konnten nicht gespeichert werden.",
      );
    } finally {
      setConnectionSaving(false);
    }
  }

  async function onSaveReceipt(e: React.FormEvent) {
    e.preventDefault();
    if (!canWrite) return;
    setReceiptSaving(true);
    setReceiptSuccess(false);
    setError(null);
    try {
      await saveReceiptConfig();
      setReceiptSuccess(true);
      await loadSettings();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Bon-Einstellungen konnten nicht gespeichert werden.");
    } finally {
      setReceiptSaving(false);
    }
  }

  async function onLogoSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !canWrite) return;
    setLogoUploading(true);
    setError(null);
    try {
      const dataUrl = await compressReceiptLogoToDataUrl(file);
      setLogoData(dataUrl);
      setLogoRemoved(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Logo konnte nicht geladen werden.");
    } finally {
      setLogoUploading(false);
    }
  }

  async function saveReceiptConfig() {
    const body: Record<string, unknown> = {
      header_text: headerText.trim() || null,
      footer_text: footerText.trim() || null,
      characters_per_line: charactersPerLine,
      separator_style: separatorStyle,
      header_align: headerAlign,
      footer_align: footerAlign,
      title_text: titleText.trim() || null,
      font_size: fontSize,
      font_family: fontFamily,
      logo_width_percent: logoWidthPercent,
    };
    if (logoData.trim()) {
      body.logo_data = logoData.trim();
    } else if (logoRemoved) {
      body.logo_data = "";
    }
    await api.patch("/api/v1/settings/receipt-config", body);
  }

  async function savePickupReceiptConfig() {
    const body: Record<string, unknown> = {
      header_text: pickupHeaderText.trim() || null,
      footer_text: pickupFooterText.trim() || null,
      characters_per_line: pickupCharactersPerLine,
      separator_style: pickupSeparatorStyle,
      header_align: pickupHeaderAlign,
      footer_align: pickupFooterAlign,
      title_text: pickupTitleText.trim() || null,
      font_size: pickupFontSize,
      font_family: pickupFontFamily,
      logo_width_percent: pickupLogoWidthPercent,
    };
    if (pickupLogoData.trim()) {
      body.logo_data = pickupLogoData.trim();
    } else if (pickupLogoRemoved) {
      body.logo_data = "";
    }
    await api.patch("/api/v1/settings/pickup-receipt-config", body);
  }

  async function onSavePickupReceipt(e: React.FormEvent) {
    e.preventDefault();
    if (!canWrite) return;
    setPickupReceiptSaving(true);
    setPickupReceiptSuccess(false);
    setError(null);
    try {
      await savePickupReceiptConfig();
      setPickupReceiptSuccess(true);
      await loadSettings();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Abholungsbon-Einstellungen konnten nicht gespeichert werden.");
    } finally {
      setPickupReceiptSaving(false);
    }
  }

  async function onPickupLogoSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !canWrite) return;
    setPickupLogoUploading(true);
    setError(null);
    try {
      const dataUrl = await compressReceiptLogoToDataUrl(file);
      setPickupLogoData(dataUrl);
      setPickupLogoRemoved(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Logo konnte nicht geladen werden.");
    } finally {
      setPickupLogoUploading(false);
    }
  }

  async function onPrintPickupTest() {
    if (!canWrite) return;
    setPickupPrintTesting(true);
    setPickupPrintResult(null);
    setError(null);
    try {
      await savePickupReceiptConfig();
      const result = await api.post<{ success: boolean; message: string }>(
        "/api/v1/settings/device-service/print-pickup-test",
        {},
      );
      setPickupPrintResult(result.message);
    } catch (err) {
      setPickupPrintResult(err instanceof Error ? err.message : "Testdruck fehlgeschlagen");
    } finally {
      setPickupPrintTesting(false);
    }
  }

  async function onPrintTest() {
    if (!canWrite) return;
    setPrintTesting(true);
    setPrintResult(null);
    setError(null);
    try {
      await saveReceiptConfig();
      const result = await api.post<{ success: boolean; message: string }>(
        "/api/v1/settings/device-service/print-test",
        { include_card_payment: includeCardPayment },
      );
      setPrintResult(result.message);
    } catch (err) {
      setPrintResult(err instanceof Error ? err.message : "Testdruck fehlgeschlagen");
    } finally {
      setPrintTesting(false);
    }
  }

  function clearLogo() {
    setLogoData("");
    setLogoRemoved(true);
  }

  function clearPickupLogo() {
    setPickupLogoData("");
    setPickupLogoRemoved(true);
  }

  const receiptHasLogo = Boolean(logoData) || (logoConfiguredOnServer && !logoRemoved);
  const pickupHasLogo = Boolean(pickupLogoData) || (pickupLogoConfiguredOnServer && !pickupLogoRemoved);

  if (loading) {
    return <p className="text-sm text-label-secondary">Laden…</p>;
  }

  const connectionConfigured = isDeviceServiceConfigured({ enable_zvt: false, base_url: baseUrl, api_key_configured: apiKeyConfigured });
  const connectionHint = getDeviceServiceConnectionHint(baseUrl, status?.error);
  const healthTone = status?.connected && status.health
    ? deviceStatusTone(status.health.status)
    : "red";

  return (
    <div className="space-y-6">
      <Card title="CampingDeviceService" bodyClassName="space-y-4">
        <p className="text-sm text-label-secondary">
          Der lokale <strong>CampingDeviceService</strong> verbindet CampDesk mit Bondrucker, EC-Terminal
          und weiteren Peripheriegeräten an dieser Workstation. Er muss auf dem Kassen-PC installiert und
          gestartet sein (Standard:{" "}
          <code className="text-xs">http://127.0.0.1:5050</code>). Hardware-Details wie Druckername und
          Terminal-IP konfigurieren Sie im{" "}
          <a
            href="http://127.0.0.1:5050/setup"
            target="_blank"
            rel="noreferrer"
            className="text-accent underline"
          >
            lokalen Setup
          </a>
          .
        </p>

        <div className="rounded-lg border border-separator bg-grouped p-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-medium text-label">Verbindungsstatus</h3>
            <Button type="button" variant="ghost" size="sm" loading={statusLoading} onClick={() => void loadStatus()}>
              Aktualisieren
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(
                status?.connected ? "green" : "red",
              )}`}
            >
              {status?.connected ? "Verbunden" : "Nicht verbunden"}
            </span>
            {status?.health ? (
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(healthTone)}`}>
                {formatDeviceStatus(status.health.status)}
                {status.health.version ? ` (v${status.health.version})` : ""}
              </span>
            ) : null}
            {status?.health?.workstation_id ? (
              <span className="text-xs text-label-secondary">
                Workstation: {status.health.workstation_id}
              </span>
            ) : null}
          </div>

          {status?.error ? <p className="text-sm text-status-red">{status.error}</p> : null}
          {connectionHint ? (
            <p className="text-sm text-label-secondary">{connectionHint}</p>
          ) : null}

          {status?.devices && status.devices.length > 0 ? (
            <div className="grid gap-2 sm:grid-cols-2">
              {status.devices.map((device) => {
                const tone = deviceStatusTone(device.status);
                return (
                  <div
                    key={device.id}
                    className="rounded-md border border-separator bg-card px-3 py-2"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium text-label">{device.name}</p>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(tone)}`}>
                        {formatDeviceStatus(device.status)}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-label-secondary">
                      {device.enabled ? "Aktiviert" : "Deaktiviert"}
                    </p>
                  </div>
                );
              })}
            </div>
          ) : connectionConfigured && status?.connected ? (
            <p className="text-sm text-label-secondary">Keine Geräteinformationen verfügbar.</p>
          ) : null}
        </div>

        <form onSubmit={(e) => void onSaveConnection(e)} className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm">
              <span className="mb-1 block text-label-secondary">Service-URL (diese Workstation)</span>
              <input
                className="eh-input"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="http://127.0.0.1:5050"
                disabled={!canWrite}
              />
              <span className="mt-1 block text-xs text-label-secondary">
                Nur bei CampDesk auf demselben PC: 127.0.0.1. Sonst die IP dieses Kassen-PCs, z. B. http://10.1.51.51:5050
                (LAN-Zugriff im lokalen Setup aktivieren).
              </span>
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-label-secondary">
                API-Key
                {apiKeyConfigured ? " (gespeichert)" : ""}
              </span>
              <input
                type="password"
                className="eh-input"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={apiKeyConfigured ? "Leer lassen = unverändert" : ""}
                disabled={!canWrite}
                autoComplete="new-password"
              />
            </label>
          </div>
          {connectionSuccess ? (
            <p className="text-sm text-status-green">Verbindungseinstellungen gespeichert.</p>
          ) : null}
          {canWrite ? (
            <div className="flex justify-end">
              <Button type="submit" loading={connectionSaving}>
                Verbindung speichern
              </Button>
            </div>
          ) : null}
        </form>
      </Card>

      <Card title="Kassenbon-Beleg" bodyClassName="space-y-4">
        <p className="text-sm text-label-secondary">
          Kopf- und Fußtext sowie ein Logo werden auf gedruckten Kassenbons verwendet. Die Vorschau zeigt
          einen rechtssicheren Musterbon mit MwSt-Aufstellung, Pflichtangaben und TSE-Testdaten (Stub).
          Schriftgröße und -art gelten für den ESC/POS-Druck.
        </p>

        <form onSubmit={(e) => void onSaveReceipt(e)} className="space-y-4">
          <div className="space-y-2">
            <label className="flex flex-col gap-1 text-sm" htmlFor="receipt-header-text">
              Kopftext (z. B. Campingplatzname, Adresse)
            </label>
            <ReceiptTextToolbar
              textareaId="receipt-header-text"
              value={headerText}
              onChange={setHeaderText}
              disabled={!canWrite}
            />
            <textarea
              id="receipt-header-text"
              className="eh-input min-h-[4rem] resize-y"
              rows={3}
              value={headerText}
              onChange={(e) => setHeaderText(e.target.value)}
              disabled={!canWrite}
              placeholder={"**Campingplatz Sonne**\nMusterstraße 1\n12345 Musterstadt"}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm">
              Kopfbereich-Ausrichtung
              <select
                className="eh-input"
                value={headerAlign}
                onChange={(e) => setHeaderAlign(e.target.value as ReceiptTextAlignment)}
                disabled={!canWrite}
              >
                <option value="center">Zentriert</option>
                <option value="left">Linksbündig</option>
                <option value="right">Rechtsbündig</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Belegtitel
              <input
                className="eh-input"
                value={titleText}
                onChange={(e) => setTitleText(e.target.value)}
                disabled={!canWrite}
                placeholder={DEFAULT_RECEIPT_TITLE_TEXT}
              />
              <span className="text-xs text-label-secondary">
                Wird auf Testbons in Großbuchstaben gedruckt (z. B. KASSENBON, TESTBELEG).
              </span>
            </label>
          </div>

          <div className="space-y-2">
            <span className="text-sm text-label-secondary">Logo (optional)</span>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml,.svg"
              className="hidden"
              disabled={!canWrite || logoUploading}
              onChange={(e) => void onLogoSelected(e)}
            />
            <div className="flex flex-wrap items-center gap-2">
              {canWrite ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  loading={logoUploading}
                  disabled={logoUploading}
                  onClick={() => logoInputRef.current?.click()}
                >
                  {receiptHasLogo ? "Logo ersetzen" : "Logo hochladen"}
                </Button>
              ) : null}
              {receiptHasLogo && canWrite ? (
                <Button type="button" variant="ghost" size="sm" onClick={clearLogo}>
                  Entfernen
                </Button>
              ) : null}
              {receiptHasLogo ? (
                <span className="text-sm text-status-green">Logo hinterlegt – Druckdarstellung in der Bon-Vorschau</span>
              ) : (
                <span className="text-sm text-label-tertiary">Kein Logo hinterlegt</span>
              )}
            </div>
            {receiptHasLogo ? (
              <label className="flex flex-col gap-1 text-sm max-w-[14rem]">
                Logo-Breite auf dem Bon
                <select
                  className="eh-input"
                  value={logoWidthPercent}
                  onChange={(e) => setLogoWidthPercent(Number(e.target.value))}
                  disabled={!canWrite}
                >
                  {RECEIPT_LOGO_WIDTH_PERCENT_OPTIONS.map((percent) => (
                    <option key={percent} value={percent}>
                      {percent} % der Bonbreite
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </div>

          <div className="space-y-2">
            <label className="flex flex-col gap-1 text-sm" htmlFor="receipt-footer-text">
              Fußtext (z. B. Steuernummer, Danksagung)
            </label>
            <ReceiptTextToolbar
              textareaId="receipt-footer-text"
              value={footerText}
              onChange={setFooterText}
              disabled={!canWrite}
            />
            <textarea
              id="receipt-footer-text"
              className="eh-input min-h-[4rem] resize-y"
              rows={3}
              value={footerText}
              onChange={(e) => setFooterText(e.target.value)}
              disabled={!canWrite}
              placeholder={"USt-IdNr.: DE123456789\n*Vielen Dank für Ihren Besuch!*"}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm">
              Fußbereich-Ausrichtung
              <select
                className="eh-input"
                value={footerAlign}
                onChange={(e) => setFooterAlign(e.target.value as ReceiptTextAlignment)}
                disabled={!canWrite}
              >
                <option value="left">Linksbündig</option>
                <option value="center">Zentriert</option>
                <option value="right">Rechtsbündig</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Trennlinien-Stil
              <select
                className="eh-input"
                value={separatorStyle}
                onChange={(e) => setSeparatorStyle(e.target.value as ReceiptSeparatorStyle)}
                disabled={!canWrite}
              >
                <option value="double">Doppellinie (=)</option>
                <option value="single">Einfach (-)</option>
                <option value="dots">Punkte (·)</option>
              </select>
            </label>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm">
              Schriftgröße
              <select
                className="eh-input"
                value={fontSize}
                onChange={(e) => setFontSize(e.target.value as ReceiptFontSize)}
                disabled={!canWrite}
              >
                <option value="small">Klein</option>
                <option value="normal">Normal</option>
                <option value="large">Groß</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Schriftart
              <select
                className="eh-input"
                value={fontFamily}
                onChange={(e) => setFontFamily(e.target.value as ReceiptFontFamily)}
                disabled={!canWrite}
              >
                <option value="arial">Arial</option>
                <option value="condensed">Kompakt (Font B)</option>
              </select>
            </label>
          </div>

          <label className="flex flex-col gap-1 text-sm">
            Zeichen pro Zeile (Bonbreite)
            <input
              type="number"
              className="eh-input max-w-[8rem]"
              min={MIN_RECEIPT_CHARACTERS_PER_LINE}
              max={MAX_RECEIPT_CHARACTERS_PER_LINE}
              step={1}
              value={charactersPerLine}
              onChange={(e) => setCharactersPerLine(Number(e.target.value) || DEFAULT_RECEIPT_CHARACTERS_PER_LINE)}
              disabled={!canWrite}
            />
            <span className="text-xs text-label-secondary">
              Typisch 32 für 58&nbsp;mm-Rollen, 42–48 für 80&nbsp;mm. Wird im lokalen Setup aus dem Treiber vorgeschlagen und kann hier korrigiert werden.
            </span>
          </label>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="rounded border-separator text-accent focus:ring-accent"
              checked={includeCardPayment}
              onChange={(e) => setIncludeCardPayment(e.target.checked)}
              disabled={!canWrite}
            />
            Kartenzahlungsinformationen auf dem Bon anzeigen (Testdaten)
          </label>

          <ReceiptPreview
            headerText={headerText}
            footerText={footerText}
            logoData={logoData || undefined}
            includeCardPayment={includeCardPayment}
            charactersPerLine={charactersPerLine}
            separatorStyle={separatorStyle}
            headerAlign={headerAlign}
            footerAlign={footerAlign}
            titleText={titleText}
            fontSize={fontSize}
            fontFamily={fontFamily}
            logoWidthPercent={logoWidthPercent}
          />

          {receiptSuccess ? (
            <p className="text-sm text-status-green">Bon-Einstellungen gespeichert.</p>
          ) : null}

          <div className="flex flex-wrap items-center justify-between gap-2">
            {canWrite ? (
              <Button type="button" variant="secondary" loading={printTesting} onClick={() => void onPrintTest()}>
                Vorschau drucken
              </Button>
            ) : null}
            {canWrite ? (
              <Button type="submit" loading={receiptSaving}>
                Bon speichern
              </Button>
            ) : null}
          </div>
          {printResult ? (
            <p className={`text-sm ${printResult.includes("fehl") ? "text-status-red" : "text-label-secondary"}`}>
              {printResult}
            </p>
          ) : null}
        </form>
      </Card>

      <Card title="Abholungsbon-Beleg" bodyClassName="space-y-4">
        <p className="text-sm text-label-secondary">
          Kopf- und Fußtext sowie ein Logo werden auf gedruckten Abholungsbons verwendet. Die Vorschau zeigt
          einen Muster-Abholbeleg mit Gast, Platz und Artikeln ohne Preise.
        </p>

        <form onSubmit={(e) => void onSavePickupReceipt(e)} className="space-y-4">
          <div className="space-y-2">
            <label className="flex flex-col gap-1 text-sm" htmlFor="pickup-receipt-header-text">
              Kopftext (z. B. Campingplatzname, Adresse)
            </label>
            <ReceiptTextToolbar
              textareaId="pickup-receipt-header-text"
              value={pickupHeaderText}
              onChange={setPickupHeaderText}
              disabled={!canWrite}
            />
            <textarea
              id="pickup-receipt-header-text"
              className="eh-input min-h-[4rem] resize-y"
              rows={3}
              value={pickupHeaderText}
              onChange={(e) => setPickupHeaderText(e.target.value)}
              disabled={!canWrite}
              placeholder={"**Campingplatz Sonne**\nMusterstraße 1\n12345 Musterstadt"}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm">
              Kopfbereich-Ausrichtung
              <select
                className="eh-input"
                value={pickupHeaderAlign}
                onChange={(e) => setPickupHeaderAlign(e.target.value as ReceiptTextAlignment)}
                disabled={!canWrite}
              >
                <option value="center">Zentriert</option>
                <option value="left">Linksbündig</option>
                <option value="right">Rechtsbündig</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Belegtitel
              <input
                className="eh-input"
                value={pickupTitleText}
                onChange={(e) => setPickupTitleText(e.target.value)}
                disabled={!canWrite}
                placeholder={DEFAULT_PICKUP_RECEIPT_TITLE_TEXT}
              />
              <span className="text-xs text-label-secondary">
                Wird auf Abholbons in Großbuchstaben gedruckt (z. B. ABHOLBELEG).
              </span>
            </label>
          </div>

          <div className="space-y-2">
            <span className="text-sm text-label-secondary">Logo (optional)</span>
            <input
              ref={pickupLogoInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml,.svg"
              className="hidden"
              disabled={!canWrite || pickupLogoUploading}
              onChange={(e) => void onPickupLogoSelected(e)}
            />
            <div className="flex flex-wrap items-center gap-2">
              {canWrite ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  loading={pickupLogoUploading}
                  disabled={pickupLogoUploading}
                  onClick={() => pickupLogoInputRef.current?.click()}
                >
                  {pickupHasLogo ? "Logo ersetzen" : "Logo hochladen"}
                </Button>
              ) : null}
              {pickupHasLogo && canWrite ? (
                <Button type="button" variant="ghost" size="sm" onClick={clearPickupLogo}>
                  Entfernen
                </Button>
              ) : null}
              {pickupHasLogo ? (
                <span className="text-sm text-status-green">Logo hinterlegt – Druckdarstellung in der Bon-Vorschau</span>
              ) : (
                <span className="text-sm text-label-tertiary">Kein Logo hinterlegt</span>
              )}
            </div>
            {pickupHasLogo ? (
              <label className="flex flex-col gap-1 text-sm max-w-[14rem]">
                Logo-Breite auf dem Bon
                <select
                  className="eh-input"
                  value={pickupLogoWidthPercent}
                  onChange={(e) => setPickupLogoWidthPercent(Number(e.target.value))}
                  disabled={!canWrite}
                >
                  {RECEIPT_LOGO_WIDTH_PERCENT_OPTIONS.map((percent) => (
                    <option key={percent} value={percent}>
                      {percent} % der Bonbreite
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </div>

          <div className="space-y-2">
            <label className="flex flex-col gap-1 text-sm" htmlFor="pickup-receipt-footer-text">
              Fußtext (z. B. Hinweise zur Abholung)
            </label>
            <ReceiptTextToolbar
              textareaId="pickup-receipt-footer-text"
              value={pickupFooterText}
              onChange={setPickupFooterText}
              disabled={!canWrite}
            />
            <textarea
              id="pickup-receipt-footer-text"
              className="eh-input min-h-[4rem] resize-y"
              rows={3}
              value={pickupFooterText}
              onChange={(e) => setPickupFooterText(e.target.value)}
              disabled={!canWrite}
              placeholder={"Bitte an der Rezeption abholen.\n*Vielen Dank!*"}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm">
              Fußbereich-Ausrichtung
              <select
                className="eh-input"
                value={pickupFooterAlign}
                onChange={(e) => setPickupFooterAlign(e.target.value as ReceiptTextAlignment)}
                disabled={!canWrite}
              >
                <option value="left">Linksbündig</option>
                <option value="center">Zentriert</option>
                <option value="right">Rechtsbündig</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Trennlinien-Stil
              <select
                className="eh-input"
                value={pickupSeparatorStyle}
                onChange={(e) => setPickupSeparatorStyle(e.target.value as ReceiptSeparatorStyle)}
                disabled={!canWrite}
              >
                <option value="double">Doppellinie (=)</option>
                <option value="single">Einfach (-)</option>
                <option value="dots">Punkte (·)</option>
              </select>
            </label>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm">
              Schriftgröße
              <select
                className="eh-input"
                value={pickupFontSize}
                onChange={(e) => setPickupFontSize(e.target.value as ReceiptFontSize)}
                disabled={!canWrite}
              >
                <option value="small">Klein</option>
                <option value="normal">Normal</option>
                <option value="large">Groß</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Schriftart
              <select
                className="eh-input"
                value={pickupFontFamily}
                onChange={(e) => setPickupFontFamily(e.target.value as ReceiptFontFamily)}
                disabled={!canWrite}
              >
                <option value="arial">Arial</option>
                <option value="condensed">Kompakt (Font B)</option>
              </select>
            </label>
          </div>

          <label className="flex flex-col gap-1 text-sm">
            Zeichen pro Zeile (Bonbreite)
            <input
              type="number"
              className="eh-input max-w-[8rem]"
              min={MIN_RECEIPT_CHARACTERS_PER_LINE}
              max={MAX_RECEIPT_CHARACTERS_PER_LINE}
              step={1}
              value={pickupCharactersPerLine}
              onChange={(e) =>
                setPickupCharactersPerLine(Number(e.target.value) || DEFAULT_RECEIPT_CHARACTERS_PER_LINE)
              }
              disabled={!canWrite}
            />
          </label>

          <PickupReceiptPreview
            headerText={pickupHeaderText}
            footerText={pickupFooterText}
            logoData={pickupLogoData || undefined}
            charactersPerLine={pickupCharactersPerLine}
            separatorStyle={pickupSeparatorStyle}
            headerAlign={pickupHeaderAlign}
            footerAlign={pickupFooterAlign}
            titleText={pickupTitleText}
            fontSize={pickupFontSize}
            fontFamily={pickupFontFamily}
            logoWidthPercent={pickupLogoWidthPercent}
          />

          {pickupReceiptSuccess ? (
            <p className="text-sm text-status-green">Abholungsbon-Einstellungen gespeichert.</p>
          ) : null}

          <div className="flex flex-wrap items-center justify-between gap-2">
            {canWrite ? (
              <Button
                type="button"
                variant="secondary"
                loading={pickupPrintTesting}
                onClick={() => void onPrintPickupTest()}
              >
                Vorschau drucken
              </Button>
            ) : null}
            {canWrite ? (
              <Button type="submit" loading={pickupReceiptSaving}>
                Abholungsbon speichern
              </Button>
            ) : null}
          </div>
          {pickupPrintResult ? (
            <p
              className={`text-sm ${pickupPrintResult.includes("fehl") ? "text-status-red" : "text-label-secondary"}`}
            >
              {pickupPrintResult}
            </p>
          ) : null}
        </form>
      </Card>

      {error ? <p className="text-sm text-status-red">{error}</p> : null}
    </div>
  );
}
